import Input from '../ui/Input';
import Select from '../ui/Select';

/**
 * The dynamic-field builder row — shared by the workflow "Edit step" modal and the
 * Nationalities master modal. Renders one field definition with its type, options and
 * validation controls, plus (for options fields) any conditional fields nested under each
 * answer. Pairs with <DynamicField /> / <FieldAnswerList /> which render the fields for
 * answering.
 *
 * Fields are kept as one flat array (`fields`); a conditional field just carries a
 * `depends_on: { field_id, value }` pointing at the option that reveals it. `onFieldsChange`
 * always receives the whole updated array back — this component owns finding/mutating a
 * field's descendants (cascading a delete, a type change, or an option rename/removal) so
 * callers never have to know about the nesting.
 */

export const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'checkbox', label: 'Check box' },
  { value: 'radio', label: 'Radio button' },
  { value: 'select', label: 'Select field' },
  { value: 'multiselect', label: 'Multi select field' },
  { value: 'date', label: 'Date' },
];

export const OPTION_TYPES = ['checkbox', 'radio', 'select', 'multiselect'];

export const DATE_RULE_OPERATORS = [
  { value: 'before', label: 'Less than' },
  { value: 'after', label: 'Greater than' },
];

export const DATE_RULE_REFERENCES = [
  { value: 'today', label: 'Today' },
  { value: '1_week', label: 'One week from today' },
  { value: '1_month', label: 'One month from today' },
  { value: '1_year', label: 'One year from today' },
];

export function newId() {
  return typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `f_${Date.now()}_${Math.random()}`;
}

export function emptyField() {
  return {
    id: newId(),
    label: '',
    type: 'text',
    options: [],
    validation: { required: false, date_rule: null },
    show_in_basic_details: false,
    depends_on: null,
  };
}

function childFieldIds(fields, parentId) {
  const direct = fields.filter((f) => f.depends_on?.field_id === parentId).map((f) => f.id);
  return direct.reduce((acc, id) => [...acc, ...childFieldIds(fields, id)], direct);
}

/** Remove a field and, recursively, every conditional field nested under it. */
function removeFieldCascade(fields, id) {
  const doomed = new Set([id, ...childFieldIds(fields, id)]);
  return fields.filter((f) => !doomed.has(f.id));
}

/** Top-level "+ Add field" — always a plain, unconditional field. */
export function addTopLevelField(fields) {
  return [...fields, emptyField()];
}

export default function FieldEditor({ field, fields, onFieldsChange, showBasicDetailsOption = true, depth = 0 }) {
  const hasOptions = OPTION_TYPES.includes(field.type);
  const isDate = field.type === 'date';
  const hasDateRule = isDate && !!field.validation.date_rule;
  const children = fields.filter((f) => f.depends_on?.field_id === field.id);

  const replaceSelf = (next) => onFieldsChange(fields.map((f) => (f.id === field.id ? next : f)));
  const removeSelf = () => onFieldsChange(removeFieldCascade(fields, field.id));

  const setType = (type) => {
    const stillHasOptions = OPTION_TYPES.includes(type);
    // Switching away from an options type orphans anything conditioned on this field's old
    // options — drop that whole branch rather than leave dangling conditional fields.
    const withoutOrphans = stillHasOptions ? fields : fields.filter((f) => !childFieldIds(fields, field.id).includes(f.id));
    onFieldsChange(
      withoutOrphans.map((f) =>
        f.id === field.id
          ? {
              ...field,
              type,
              options: stillHasOptions ? (field.options.length ? field.options : ['', '']) : [],
              validation: { required: field.validation.required, date_rule: type === 'date' ? field.validation.date_rule : null },
            }
          : f
      )
    );
  };

  const setOption = (idx, value) => {
    const oldValue = field.options[idx];
    const options = [...field.options];
    options[idx] = value;
    const next = fields.map((f) => (f.id === field.id ? { ...field, options } : f));
    // Keep conditional fields pointed at the right option when its label changes.
    onFieldsChange(
      next.map((f) => (f.depends_on?.field_id === field.id && f.depends_on.value === oldValue ? { ...f, depends_on: { ...f.depends_on, value } } : f))
    );
  };

  const addOption = () => replaceSelf({ ...field, options: [...field.options, ''] });

  const removeOption = (idx) => {
    const value = field.options[idx];
    const withoutOption = fields.map((f) => (f.id === field.id ? { ...field, options: field.options.filter((_, i) => i !== idx) } : f));
    // The option is gone, so any field conditioned on it is now orphaned — drop those too.
    const orphans = withoutOption.filter((f) => f.depends_on?.field_id === field.id && f.depends_on.value === value);
    onFieldsChange(orphans.reduce((acc, orphan) => removeFieldCascade(acc, orphan.id), withoutOption));
  };

  const addChildField = (optionValue) => onFieldsChange([...fields, { ...emptyField(), depends_on: { field_id: field.id, value: optionValue } }]);

  const setRequired = (required) => replaceSelf({ ...field, validation: { ...field.validation, required } });

  const toggleDateRule = (enabled) =>
    replaceSelf({
      ...field,
      validation: { ...field.validation, date_rule: enabled ? { operator: 'after', reference: 'today' } : null },
    });

  const setDateRule = (patch) =>
    replaceSelf({ ...field, validation: { ...field.validation, date_rule: { ...field.validation.date_rule, ...patch } } });

  return (
    <div className={depth ? 'rounded-md border border-gray-200 bg-gray-50/60 p-3' : 'rounded-md border border-gray-200 p-3'}>
      <div className="flex items-start gap-2">
        <Input
          className="flex-1"
          placeholder="Field label"
          value={field.label}
          onChange={(e) => replaceSelf({ ...field, label: e.target.value })}
        />
        <Select className="w-44 shrink-0" value={field.type} onChange={(e) => setType(e.target.value)}>
          {FIELD_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </Select>
        <button type="button" onClick={removeSelf} className="mt-2 shrink-0 text-xs text-red-500 hover:underline">
          Remove
        </button>
      </div>

      {hasOptions && (
        <div className="mt-2 space-y-2 pl-1">
          <p className="text-xs font-medium text-gray-500">Answer options</p>
          {field.options.map((opt, idx) => {
            const optionChildren = children.filter((c) => c.depends_on.value === opt);
            return (
              <div key={idx} className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <Input className="flex-1" placeholder={`Option ${idx + 1}`} value={opt} onChange={(e) => setOption(idx, e.target.value)} />
                  {field.options.length > 2 && (
                    <button type="button" onClick={() => removeOption(idx)} className="text-xs text-gray-400 hover:text-red-600">
                      ✕
                    </button>
                  )}
                </div>
                {opt.trim() && (
                  <div className="space-y-1.5 border-l-2 border-gray-200 pl-3">
                    {optionChildren.map((child) => (
                      <FieldEditor
                        key={child.id}
                        field={child}
                        fields={fields}
                        onFieldsChange={onFieldsChange}
                        showBasicDetailsOption={showBasicDetailsOption}
                        depth={depth + 1}
                      />
                    ))}
                    <button type="button" onClick={() => addChildField(opt)} className="text-xs text-primary hover:underline">
                      + Add field shown when "{opt}" is selected
                    </button>
                  </div>
                )}
              </div>
            );
          })}
          <button type="button" onClick={addOption} className="text-xs text-primary hover:underline">
            + Add option
          </button>
        </div>
      )}

      <div className="mt-2.5 space-y-1.5 border-t border-gray-100 pt-2 pl-1">
        <p className="text-xs font-medium text-gray-500">Validation</p>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" className="accent-primary" checked={field.validation.required} onChange={(e) => setRequired(e.target.checked)} />
          Required
        </label>

        {isDate && (
          <div className="space-y-1.5">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" className="accent-primary" checked={hasDateRule} onChange={(e) => toggleDateRule(e.target.checked)} />
              Date must be relative to today
            </label>
            {hasDateRule && (
              <div className="flex items-center gap-2 pl-6">
                <Select className="w-36" value={field.validation.date_rule.operator} onChange={(e) => setDateRule({ operator: e.target.value })}>
                  {DATE_RULE_OPERATORS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
                <Select className="w-48" value={field.validation.date_rule.reference} onChange={(e) => setDateRule({ reference: e.target.value })}>
                  {DATE_RULE_REFERENCES.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </Select>
              </div>
            )}
          </div>
        )}

        {showBasicDetailsOption && (
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              className="accent-primary"
              checked={!!field.show_in_basic_details}
              onChange={(e) => replaceSelf({ ...field, show_in_basic_details: e.target.checked })}
            />
            Show in candidate basic details
          </label>
        )}
      </div>
    </div>
  );
}
